/* eslint-disable eqeqeq */
/* eslint-disable no-unused-vars */
// src/components/multi-select.tsx

import * as React from "react";
import { cva } from "class-variance-authority";
import {
  CheckIcon,
  XCircle,
  ChevronDown,
  XIcon,
  WandSparkles,
} from "lucide-react";

import { cn } from "../lib/utils";
import { Separator } from "./ui/separator";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import { OrganizationTypeList } from "../lib/OptionsAPI";
import { debounce } from "lodash";
import { CaretSortIcon } from "@radix-ui/react-icons";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  // "m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300",
  "m-1 transition ease-in-out delay-150 duration-300",
  {
    variants: {
      // variant: {
      //   default:
      //     "border-foreground/10 text-foreground bg-card hover:bg-card/80",
      //   secondary:
      //     "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
      //   destructive:
      //     "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
      //   inverted: "inverted",
      // },
      variant: {
        default: "border-foreground/10 text-foreground bg-card",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export const OrganizationTypeSelectMulti = React.forwardRef(
  (
    {
      options: Options,
      onValueChange,
      variant,
      defaultValue = [],
      placeholder = "Select options",
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      className,
      setOrganizationTypeList,
      setOrganizationTypeLoading,
      handleBlur,
      ...props
    },
    ref
  ) => {
    let options = Options?.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.value === item.value)
    );
    const [selectedValues, setSelectedValues] = React.useState(defaultValue);
    // const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [hasMore, setHasMore] = React.useState(true);
    const [searchTerm, setSearchTerm] = React.useState(""); // Store search input
    const scrollRef = React.useRef(null); // Ref for tracking scrolling

    React.useEffect(() => {
      setSelectedValues(defaultValue);
    }, [defaultValue]);

    const handleInputKeyDown = (event) => {
      if (event.key === "Enter") {
        setOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (value) => {
      // setOpen(false);
      const newSelectedValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      setOpen((prev) => !prev);
    };

    const toggleAll = () => {
      if (selectedValues?.length === options?.length) {
        handleClear();
      } else {
        const allValues = options.map((option) => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }
    };

    const isLoadingMoreRef = React.useRef(false); // Prevent concurrent calls

    const loadMore = async () => {
      if (!hasMore || isLoadingMoreRef.current) return;

      isLoadingMoreRef.current = true;
      const nextPage = page + 1;

      const moreData = await OrganizationTypeList(
        setOrganizationTypeLoading,
        setOrganizationTypeList,
        true
      );
      setPage(nextPage);
      setHasMore(moreData);

      isLoadingMoreRef.current = false;
    };

    // Manual scroll detection
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleScroll = React.useCallback(
      debounce(() => {
        if (!scrollRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

        if (scrollTop + clientHeight >= scrollHeight - 10) {
          loadMore();
        }
      }, 200), // 200ms debounce
      [page, hasMore]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchOrganizationTypes = React.useCallback(
      debounce(async (query) => {
        setPage(1); // Reset pagination on new search
        setHasMore(true);
        const moreData = await OrganizationTypeList(
          setOrganizationTypeLoading,
          setOrganizationTypeList,
          true, // Replace list on new search
          query // Pass the search query
        );
        setHasMore(moreData);
      }, 500), // 500ms delay
      []
    );

    React.useEffect(() => {
      options?.length == 0 && fetchOrganizationTypes();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (e) => {
      const query = e;
      setSearchTerm(query);
      fetchOrganizationTypes(query);
    };

    return (
      <>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              {...props}
              // onClick={handleTogglePopover}
              className={cn(
                "flex w-full p-1 rounded-md border h-9 items-center justify-between bg-inherit shadow-sm border-input hover:bg-accent hover:text-accent-foreground text-muted-foreground dark:text-muted-foreground transition-colors",
                className
              )}
              onBlur={handleBlur}
            >
              {selectedValues.length > 0 ? (
                <div className="flex justify-between items-center w-full">
                  <div className="flex flex-wrap items-center">
                    {selectedValues.slice(0, maxCount).map((value) => {
                      const option = options.find((o) => o.value === value);
                      const IconComponent = option?.icon;
                      return (
                        <Badge
                          key={value}
                          className={cn(
                            isAnimating ? "animate-bounce" : "",
                            multiSelectVariants({ variant })
                          )}
                          style={{ animationDuration: `${animation}s` }}
                        >
                          {IconComponent && (
                            <IconComponent className="h-4 w-4 mr-2" />
                          )}
                          {option?.label}
                          <XCircle
                            className="ml-2 h-4 w-4 cursor-pointer"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleOption(value);
                            }}
                          />
                        </Badge>
                      );
                    })}
                    {selectedValues.length > maxCount && (
                      <Badge
                        className={cn(
                          isAnimating ? "animate-bounce" : "",
                          multiSelectVariants({ variant })
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {/* {`+ ${selectedValues.length - maxCount}  ${placeholder}`} */}
                        {`${selectedValues.length - maxCount}  ${placeholder}`}
                        {/* <XCircle
                        className="ml-2 h-4 w-4 cursor-pointer"
                        onClick={(event) => {
                          event.stopPropagation();
                          clearExtraOptions();
                        }}
                      /> */}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <XIcon
                      className="h-4 mx-2 cursor-pointer"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClear();
                      }}
                    />
                    <Separator
                      orientation="vertical"
                      className="flex min-h-6 h-full"
                    />
                    <CaretSortIcon className="h-4 cursor-pointer opacity-50 mx-2" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between w-full mx-auto">
                  <span className="text-sm mx-2 font-normal">
                    {placeholder}
                  </span>
                  <CaretSortIcon className="h-4 cursor-pointer opacity-50 mx-2" />
                </div>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="start"
            modal={false}
            onPointerDownOutside={(e) => e.stopPropagation()}
            onFocusOutside={(e) => e.preventDefault()}
            onWheelCapture={(e) => e.stopPropagation()}
            // onEscapeKeyDown={() => setOpen(false)}
          >
            <Command>
              <CommandInput
                placeholder="Search..."
                // onKeyDown={handleInputKeyDown}
                value={searchTerm}
                onValueChange={handleSearch}
              />
              <CommandList
                ref={scrollRef}
                onScroll={handleScroll} // Attach scroll event listener
                className="max-h-60 overflow-y-auto"
              >
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    key="all"
                    onSelect={toggleAll}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        selectedValues.length === options.length
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <span>(Select All)</span>
                  </CommandItem>
                  {options.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        onSelect={() => toggleOption(option.value)}
                        className="cursor-pointer"
                      >
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50 [&_svg]:invisible"
                          )}
                        >
                          <CheckIcon className="h-4 w-4" />
                        </div>
                        {option.icon && (
                          <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                        )}
                        <span>{option.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
                <CommandSeparator />
                <CommandGroup>
                  <div className="flex items-center justify-between">
                    {selectedValues.length > 0 && (
                      <>
                        <CommandItem
                          onSelect={handleClear}
                          className="flex-1 justify-center cursor-pointer"
                        >
                          Clear
                        </CommandItem>
                        <Separator
                          orientation="vertical"
                          className="flex min-h-6 h-full"
                        />
                      </>
                    )}
                    <CommandItem
                      onSelect={() => setOpen(false)}
                      className="flex-1 justify-center cursor-pointer max-w-full"
                    >
                      Close
                    </CommandItem>
                  </div>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </>
    );
  }
);

OrganizationTypeSelectMulti.displayName = "OrganizationTypeSelectMulti";
